import Link from "next/link";

import { Button, Card, Skeleton, Snackbar } from "@chef/components";
import {
  BRAND_PATHS,
  DeliveryInterval,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
  WEEKDAY,
} from "@chef/constants";
import {
  getYear,
  getWeek,
  nextDeliveryWeekDateObj,
  formatDate,
  formatTimeInterval,
  padPostalCode,
  isMealboxBasketType,
} from "@chef/helpers";
import { Check, Plus } from "@chef/icons/small";
import {
  BillingQuery,
  useAddressValidationQuery,
  useBillingQuery,
  useCalendarQuery,
  useOrdersQuery,
} from "@chef/state-management";
import { QUERY_MODE } from "@chef/smart-components";
import { AppShell } from "@chef/feature-app-shell";

import { MyAccountLayout } from "../../components/MyAccountLayout";
import { SHARED_BRAND } from "../../utils";

import { intl } from "./MyAccountDeliveryInformationPage.Intl";

export const MyAccountDeliveryInformationPage = () => {
  const { data: billingQuery, isLoading } = useBillingQuery();

  const defaultBasket = billingQuery?.billing.baskets.find(isMealboxBasketType);
  const defaultAddress = billingQuery?.billing.addresses?.find(
    (s) => s.addressId === defaultBasket?.shippingAddressId,
  );

  const addressesWithoutDefault = billingQuery?.billing.addresses?.filter(
    (address) => address.addressId !== defaultAddress?.addressId,
  );

  return (
    <AppShell title={intl.DELIVERY_INFORMATION_TITLE}>
      <AppShell.Meta />
      <AppShell.Header />
      <AppShell.AccountPausedBanner />
      <AppShell.OldVippsBanner />

      <AppShell.Main grid>
        <MyAccountLayout page="deliveryInformation">
          <div
            className="flex flex-col col-span-full md:col-span-5"
            id="min-konto-levering-card-grid"
          >
            <h2 className="hidden mb-6 text-xl lg:inline">
              {intl.ACTIVE_ADDRESS}
            </h2>

            {isLoading && (
              <Card className="relative flex flex-col gap-4">
                <div>
                  <Skeleton className="w-20" />
                  <Skeleton className="w-20" />
                  <Skeleton className="w-40" />
                </div>

                <div>
                  <Skeleton className="w-44" />
                  <Skeleton className="w-60" />
                </div>

                <Skeleton className="w-40 !h-10" />
              </Card>
            )}

            {defaultAddress && (
              <AddressCard address={defaultAddress} isActiveAddress />
            )}

            {addressesWithoutDefault && addressesWithoutDefault.length === 0 && (
              <div className="mt-8 text-center text-grey-1 md:mt-10">
                <p>
                  <strong>{intl.ADD_NEW_ADDRESS}</strong>
                </p>
                <p>{intl.YOU_CAN_ADD_AND_SAVE_MULTIPLE_ADDRESSES}</p>
              </div>
            )}

            {addressesWithoutDefault && addressesWithoutDefault.length > 0 && (
              <>
                <h2 className="hidden mt-10 text-xl lg:inline">
                  {intl.ALL_ADDRESSES}
                </h2>

                <ul className="flex flex-col gap-4 mt-4 lg:mt-6 lg:gap-6">
                  {addressesWithoutDefault?.map((address) => (
                    <li id={address.addressId} key={address.addressId}>
                      <AddressCard address={address} />
                    </li>
                  ))}
                </ul>
              </>
            )}

            <Link
              passHref
              href={BRAND_PATHS.ADD_NEW_ADDRESS_PAGE}
              legacyBehavior
            >
              <Button
                className="self-center mt-12"
                outlined
                IconLeft={Plus}
                id="add-new-address-btn"
              >
                {intl.ADD_ADDRESS_BUTTON}
              </Button>
            </Link>
          </div>
        </MyAccountLayout>
      </AppShell.Main>
      <AppShell.TabBar />
    </AppShell>
  );
};

const AddressCard = ({
  address,
  isActiveAddress = false,
}: {
  address: BillingQuery["billing"]["addresses"][number];
  isActiveAddress?: boolean;
}) => {
  const { data: billingQuery } = useBillingQuery();

  const defaultBasket = billingQuery?.billing.baskets.find(isMealboxBasketType);

  const defaultAddress = billingQuery?.billing.addresses?.find(
    (s) => s.addressId === defaultBasket?.shippingAddressId,
  );
  const activeTimeblock = defaultAddress?.timeblocks?.find(
    (tb) => tb.timeblockId === defaultBasket?.timeblockId,
  );

  const deliveryDay =
    SHARED_BRAND.WEEKDAYS[(activeTimeblock?.weekday as WEEKDAY) || 0];

  const deliveryFrequency =
    SHARED_BRAND.DELIVERY_FREQUENCIES[
      (defaultBasket?.deliveryInterval as DeliveryInterval) || 10
    ].toLowerCase();

  const deliveryTimeRange = formatTimeInterval(
    activeTimeblock?.from,
    activeTimeblock?.to,
    "m",
    "-",
  );

  const isAliasSetByCustomer = address.address !== address.name;

  return (
    <Card className="relative flex flex-col" noPadding>
      <div className="absolute top-6 right-6">
        <Link
          href={BRAND_PATHS.EDIT_ADDRESS_PAGE(address.addressId)}
          passHref
          className="underline text-primary"
        >
          <strong>{intl.EDIT_ADDRESS}</strong>
        </Link>
      </div>

      <div className="flex flex-col gap-4 p-6" id={address.addressId}>
        <div>
          <p>
            <strong>{address.name}</strong>
          </p>

          {isAliasSetByCustomer && <p>{address.address}</p>}

          <div className="flex">
            <p>{`${padPostalCode(address.postalCode)} ${address.city}`}</p>
          </div>
          <p>
            {address.floorNumber && (
              <span>
                {intl.FLOOR} {address.floorNumber}
                {address.apartmentName && ", "}
              </span>
            )}
            {address.apartmentName && <span> {address.apartmentName}</span>}
          </p>
        </div>

        <AddApartmentSnackBar address={address} />

        {address.instructionsToDriver && (
          <div>
            <p>
              <strong>{intl.INSTRUCTIONS_TO_DRIVER}:</strong>
            </p>
            <p>{address.instructionsToDriver}</p>
          </div>
        )}

        {isActiveAddress && (
          <div>
            <p>
              <strong>{intl.DELIVERY_DAY_AND_INTERVAL}:</strong>
            </p>

            <p>{`${deliveryDay}, ${deliveryTimeRange}, ${deliveryFrequency}`}</p>
          </div>
        )}

        {isActiveAddress && (
          <Link href={BRAND_PATHS.CHANGE_ACTIVE_ADDRESS_PAGE} legacyBehavior>
            <Button primary small className="lg:self-start">
              {intl.CHANGE_ACTIVE_ADDRESS}
            </Button>
          </Link>
        )}
      </div>

      <DeliveryDisclaimer
        isActive={isActiveAddress}
        defaultAddressId={defaultAddress?.addressId}
        currentAddressId={address.addressId}
      />
    </Card>
  );
};

const DeliveryDisclaimer = ({
  isActive,
  defaultAddressId,
  currentAddressId,
}: {
  isActive: boolean;
  defaultAddressId?: string;
  currentAddressId: string;
}) => {
  const weekObj = nextDeliveryWeekDateObj();
  const week = getWeek(weekObj);
  const year = getYear(weekObj);

  const { data: calendarQuery } = useCalendarQuery({
    week,
    year,
    range: 1,
  });
  const { data: ordersQuery } = useOrdersQuery({
    orderTypes: [ORDER_TYPE_IDS.RECURRING],
    orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.CREATED],
    count: 1,
  });
  const upcomingOrder = ordersQuery?.orders.find(() => true);

  const addressBeingDeliveredToIsNotDefault =
    upcomingOrder &&
    upcomingOrder.deliveryAddress.addressId !== defaultAddressId;

  const isDeliveringToCurrentAddress =
    upcomingOrder &&
    currentAddressId === upcomingOrder.deliveryAddress.addressId;

  const shippingDetails = calendarQuery?.calendar
    .find(() => true)
    ?.baskets.find(isMealboxBasketType)?.shippingDetails;

  const showOngoingDeliveryDisclaimer =
    !isActive &&
    addressBeingDeliveredToIsNotDefault &&
    isDeliveringToCurrentAddress;

  const showNextDeliveryDisclaimer =
    isActive && addressBeingDeliveredToIsNotDefault;

  const upcomingDeliveryDate = upcomingOrder?.deliveryDate;

  const nextDeliveryDate =
    shippingDetails?.plannedDeliveryInformation.deliveryDate;

  if (!nextDeliveryDate || !upcomingDeliveryDate) {
    return null;
  }

  if (showOngoingDeliveryDisclaimer) {
    return (
      <div className="px-6 py-4 text-xs bg-grey-2">
        {intl.ONGOING_DELIVERY_DISCLAIMER}{" "}
        {formatDate(upcomingDeliveryDate, "dd. LLL")}
      </div>
    );
  }

  if (showNextDeliveryDisclaimer) {
    return (
      <div className="flex items-center gap-2 px-6 py-4 text-xs bg-grey-2">
        <Check />

        <p>
          {intl.NEXT_DELIVERY_DISCLAIMER}{" "}
          {formatDate(nextDeliveryDate, "dd. LLL")}
        </p>
      </div>
    );
  }

  return null;
};

const AddApartmentSnackBar = ({
  address,
}: {
  address: BillingQuery["billing"]["addresses"][number];
}) => {
  const skip =
    address.streetNumber === null ||
    address.streetName === null ||
    address.addressCode === null ||
    !!address.apartmentName;

  const { data: apartmentData } = useAddressValidationQuery(
    {
      mode: QUERY_MODE.APARTMENT,
      streetName: address.streetName,
      streetNumber:
        address.streetNumber !== null ? address.streetNumber.toString() : "",
      postalCode: address.postalCode.toString(),
      letterExtra: address.letterExtra !== null ? address.letterExtra : "",
      postalArea: address.city,
    },
    {
      skip,
    },
  );

  const addressData = apartmentData?.value.find(() => true)?.apartments;
  const apartmentsExistOnAddress = addressData ? addressData.length > 0 : false;
  const addressHasNoSelectedApartment = !address.apartmentName;

  if (apartmentsExistOnAddress && addressHasNoSelectedApartment) {
    return (
      <Snackbar warning showIcon={false} className="text-sm">
        <p>
          <strong>{intl.COMPLETE_YOUR_ADDRESS_DETAILS}</strong>
        </p>
        <Link
          href={BRAND_PATHS.EDIT_ADDRESS_PAGE(address.addressId)}
          passHref
          className="underline text-primary"
        >
          {intl.ADD_APARTMENT_DETAILS}
        </Link>
      </Snackbar>
    );
  }

  return null;
};
